import type { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { getErrorMessage, globalToastManagerUI, NotificationVariants } from '@talos/kyoko';
import { isRestEndpointRejection } from './isRestEndpointRejection';

interface OnQueryStartedContext<TResult, TState = unknown> {
  dispatch: ThunkDispatch<TState, unknown, UnknownAction>;
  getState: () => TState;
  queryFulfilled: Promise<{ data: TResult }>;
}

/**
 * Higher-order function that sends a toast on query being fulfilled.
 * Shows success toast on mutation success and error toast on failure.
 * Optionally accepts an `onSuccess` callback for custom logic (e.g., dispatching actions).
 *
 * @example
 * ```ts
 * createUserApiKey: builder.mutation<Response, Params>({
 *   query: ({ userID }) => ({ method: 'POST', url: `/users/${userID}/api-key` }),
 *   onQueryStarted: sendToastOnQueryFulfilled({
 *     successMessage: ({ userName }) => `API key created for ${userName}.`,
 *     errorMessage: 'Could not create API key',
 *   }),
 * }),
 * ```
 */
export function sendToastOnQueryFulfilled<TArgs, TResult = unknown, TState = unknown>({
  successMessage,
  errorMessage,
  onSuccess,
  onError,
}: {
  successMessage?: (args: TArgs) => string;
  errorMessage?: string;
  onSuccess?: (
    data: TResult,
    context: {
      dispatch: OnQueryStartedContext<TResult, TState>['dispatch'];
      getState: OnQueryStartedContext<TResult, TState>['getState'];
    },
    args: TArgs
  ) => void;
  onError?: (
    context: {
      dispatch: OnQueryStartedContext<TResult, TState>['dispatch'];
      getState: OnQueryStartedContext<TResult, TState>['getState'];
    },
    args: TArgs
  ) => void;
}) {
  return async (args: TArgs, { dispatch, getState, queryFulfilled }: OnQueryStartedContext<TResult, TState>) => {
    try {
      const { data } = await queryFulfilled;
      if (successMessage) {
        globalToastManagerUI.addToast({
          text: successMessage(args),
          variant: NotificationVariants.Positive,
        });
      }
      onSuccess?.(data, { dispatch, getState }, args);
    } catch (rejection: unknown) {
      if (errorMessage) {
        const details = isRestEndpointRejection(rejection) ? getErrorMessage(rejection.error) : undefined;
        const errorDetails = details ? `: ${details}` : '';
        globalToastManagerUI.addToast({
          text: `${errorMessage}${errorDetails}.`,
          variant: NotificationVariants.Negative,
        });
      }
      onError?.({ dispatch, getState }, args);
    }
  };
}
